import { notification } from "antd";
import axios from "axios";
import { signInWithEmailAndPassword, signOut, type UserCredential } from "firebase/auth";
import { getAuthUserMessage } from "../auth/authTypes";
import { auth } from "../firebase";
import { createSession, destroySession, SessionLimitError } from "./session";
import { clearStoredToken, getIdToken } from "./token";

type LoginPayload = {
  email: string;
  password: string;
};

export const login = async (credentials: LoginPayload): Promise<UserCredential> => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      credentials.email,
      credentials.password,
    );

    const idToken = await userCredential.user.getIdToken();
    await createSession(idToken);

    notification.success({
      message: "Login successful",
    });

    return userCredential;
  } catch (error) {
    if (error instanceof SessionLimitError) {
      throw error;
    }

    console.error("Login failed", error);

    if (axios.isAxiosError(error) && error.response?.data) {
      const data = error.response.data;
      notification.error({
        message: "Login failed",
        description: getAuthUserMessage(data) || "Please check your credentials and try again.",
      });
    } else {
      notification.error({
        message: "Login failed",
        description: "Please check your credentials and try again.",
      });
    }

    try {
      await signOut(auth);
    } catch (signOutError) {
      console.warn("Failed to sign out after login error", signOutError);
    }

    clearStoredToken();
    throw error;
  }
};

interface LogoutOptions {
  silent?: boolean;
}

export const logout = async ({ silent = false }: LogoutOptions = {}) => {
  try {
    await destroySession();
    await signOut(auth);
    clearStoredToken();

    if (!silent) {
      notification.success({
        message: "Logged out successfully",
      });
    }
  } catch (error) {
    console.error("Logout failed", error);
    if (!silent) {
      notification.error({
        message: "Logout failed",
        description: "Please try again.",
      });
    }
  }
};

export const getToken = async (forceRefresh = false): Promise<string | null> => {
  return getIdToken(forceRefresh);
};
